import { GoogleCalendarService } from '../../api/services/GoogleCalendarService';
import { MicrosoftCalendarService } from '../../api/services/MicrosoftCalendarService';
import { ExternalCalendarService } from '../../api/services/ExternalCalendarService';
import { CalendarFeedService } from '../../api/services/CalendarFeedService';
import type { Routemize_DTOs_MicrosoftCalendarDto } from '../../api/models/Routemize_DTOs_MicrosoftCalendarDto';
import type { Routemize_DTOs_MicrosoftCalendarAuthResultDto } from '../../api/models/Routemize_DTOs_MicrosoftCalendarAuthResultDto';
import type { Routemize_DTOs_ConnectCalendarToMicrosoftDto } from '../../api/models/Routemize_DTOs_ConnectCalendarToMicrosoftDto';
import type { Routemize_DTOs_GoogleCalendarDto } from '../../api/models/Routemize_DTOs_GoogleCalendarDto';
import type { Routemize_DTOs_GoogleCalendarEventDto } from '../../api/models/Routemize_DTOs_GoogleCalendarEventDto';
import type { Routemize_DTOs_GoogleCalendarAuthResultDto } from '../../api/models/Routemize_DTOs_GoogleCalendarAuthResultDto';
import type { Routemize_DTOs_CalendarIntegrationInfoDto } from '../../api/models/Routemize_DTOs_CalendarIntegrationInfoDto';
import type { Routemize_DTOs_CalendarSyncStatusDto } from '../../api/models/Routemize_DTOs_CalendarSyncStatusDto';
import type { Routemize_DTOs_ConnectCalendarToGoogleDto } from '../../api/models/Routemize_DTOs_ConnectCalendarToGoogleDto';
import type { Routemize_DTOs_UpdateSyncSettingsDto } from '../../api/models/Routemize_DTOs_UpdateSyncSettingsDto';
import type { Routemize_Entities_Calendar_External_ExternalCalendarSyncDirection } from '../../api/models/Routemize_Entities_Calendar_External_ExternalCalendarSyncDirection';
import type { Routemize_DTOs_CalendarFeedDto } from '../../api/models/Routemize_DTOs_CalendarFeedDto';
import { Routemize_Entities_Calendar_External_ExternalCalendarType as ExternalCalendarType } from '../../api/models/Routemize_Entities_Calendar_External_ExternalCalendarType';

/**
 * CalendarManager - Business logic for calendar integration and management
 * Uses generated API services instead of raw HTTP calls
 */
export class CalendarManager {
  // Calendar integration info was fetched on nearly every page (dashboard banner,
  // bookings, calendar, profile). It changes only via the connection mutations
  // below, so cache it for the session and invalidate on those mutations.
  private integrationInfoCache: Routemize_DTOs_CalendarIntegrationInfoDto | null = null;
  private integrationInfoInFlight: Promise<Routemize_DTOs_CalendarIntegrationInfoDto> | null = null;

  /**
   * Initialize Google Calendar OAuth flow
   * @param returnUrl - URL to return to after OAuth completion
   * @returns OAuth authorization URL
   */
  async getAuthorizationUrl(returnUrl: string): Promise<string> {
    return await GoogleCalendarService.getApiAppGoogleCalendarGetAuthorizationUrl(returnUrl);
  }

  /**
   * Complete OAuth authorization and get available calendars
   * @param authCode - OAuth authorization code
   * @param stateCode - OAuth state code
   * @returns Authorization result with available calendars
   */
  async completeAuthorization(
    authCode: string,
    stateCode: string
  ): Promise<Routemize_DTOs_GoogleCalendarAuthResultDto> {
    return await GoogleCalendarService.getApiAppGoogleCalendarGetCompleteAuthorization(
      authCode,
      stateCode
    );
  }

  /**
   * Check if the current user is connected to Google Calendar
   * @returns True if connected, false otherwise
   */
  async isConnectedToGoogle(): Promise<boolean> {
    return await GoogleCalendarService.postApiAppGoogleCalendarIsConnectedToGoogle();
  }

  async isConnectedToGoogleByUserId(userId: string): Promise<boolean> {
    return await GoogleCalendarService.getApiAppGoogleCalendarGetIsConnectedToGoogleByUserId(userId);
  }

  /** Whether the given user has ANY connected calendar account (Google or Outlook). */
  async isConnectedByUserId(userId: string): Promise<boolean> {
    return await ExternalCalendarService.getApiAppExternalCalendarGetIsConnectedByUserId(userId);
  }

  /**
   * Get comprehensive calendar integration information
   * @returns Calendar integration details including sync status
   */
  async getCalendarIntegrationInfo(forceRefresh = false): Promise<Routemize_DTOs_CalendarIntegrationInfoDto> {
    if (!forceRefresh && this.integrationInfoCache) return this.integrationInfoCache;
    if (!forceRefresh && this.integrationInfoInFlight) return this.integrationInfoInFlight;
    this.integrationInfoInFlight = GoogleCalendarService.getApiAppGoogleCalendarGetCalendarIntegrationInfo()
      .then(info => { this.integrationInfoCache = info; return info; })
      .finally(() => { this.integrationInfoInFlight = null; });
    return this.integrationInfoInFlight;
  }

  /** Drop the cached integration info; call after any calendar-connection mutation. */
  invalidateIntegrationInfo(): void {
    this.integrationInfoCache = null;
    this.integrationInfoInFlight = null;
  }

  /**
   * Get list of available Google calendars for the connected account
   * @returns Array of available Google calendars
   */
  async getAvailableGoogleCalendars(): Promise<Routemize_DTOs_GoogleCalendarDto[]> {
    return await GoogleCalendarService.getApiAppGoogleCalendarGetAvailableGoogleCalendars();
  }

  /**
   * Connect a local calendar to a Google Calendar for synchronization
   * @param connectionData - Connection configuration
   */
  async connectToGoogleCalendar(
    connectionData: Routemize_DTOs_ConnectCalendarToGoogleDto
  ): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarConnectToGoogleCalendar(connectionData);
    this.invalidateIntegrationInfo();
  }

  /**
   * Disconnect a calendar from Google Calendar synchronization
   * @param calendarId - ID of the calendar to disconnect
   */
  async disconnectFromGoogleCalendar(calendarId: string): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarDisconnectFromGoogleCalendar(calendarId);
    this.invalidateIntegrationInfo();
  }

  /**
   * Revoke Google Calendar access. When googleAccountEmail is provided, only
   * that account's token is revoked; omitting it revokes all accounts.
   */
  async revokeGoogleAccess(googleAccountEmail?: string): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarRevokeGoogleAccess(googleAccountEmail);
    this.invalidateIntegrationInfo();
  }

  /**
   * Set the calendar that Routemize will write new appointment events to.
   * @param calendarId - ID of the ExternalCalendar row to make the write target
   */
  async setWriteTargetCalendar(calendarId: string): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarSetWriteTargetCalendar(calendarId);
    this.invalidateIntegrationInfo();
  }

  /**
   * Clear the write-target calendar so Routemize stops adding appointment events
   * to any Google calendar until a new target is chosen.
   */
  async clearWriteTargetCalendar(): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarClearWriteTargetCalendar();
    this.invalidateIntegrationInfo();
  }

  /**
   * Set the display color (a booking-form palette key, e.g. "lavender") for a
   * connected calendar, so its events can be rendered per-calendar.
   * @param calendarId - ID of the ExternalCalendar row
   * @param color - color key from the shared palette
   */
  async setCalendarColor(calendarId: string, color: string): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarSetCalendarColor(calendarId, color);
    this.invalidateIntegrationInfo();
  }

  /**
   * Trigger immediate synchronization for a calendar
   * @param calendarId - ID of the calendar to synchronize
   */
  async synchronizeNow(calendarId: string): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarSynchronizeNow(calendarId);
    this.invalidateIntegrationInfo();
  }

  /**
   * Toggle the active synchronization status for a calendar
   * @param calendarId - ID of the calendar to toggle
   */
  async toggleCalendarSyncActiveStatus(calendarId: string): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarToggleCalendarSyncActiveStatus(calendarId);
    this.invalidateIntegrationInfo();
  }

  /**
   * Set whether a connected calendar's events block availability (true) or are
   * display-only / synced but non-blocking (false).
   * @param calendarId - ID of the calendar to update
   * @param affectsAvailability - true to block slots, false for display-only
   */
  async setCalendarAffectsAvailability(
    calendarId: string,
    affectsAvailability: boolean
  ): Promise<void> {
    await GoogleCalendarService.postApiAppGoogleCalendarSetCalendarAffectsAvailability(
      calendarId,
      affectsAvailability
    );
    this.invalidateIntegrationInfo();
  }

  /**
   * Update synchronization settings for a calendar
   * @param settings - New synchronization settings
   */
  async updateSyncSettings(settings: Routemize_DTOs_UpdateSyncSettingsDto): Promise<void> {
    await GoogleCalendarService.putApiAppGoogleCalendarUpdateSyncSettings(settings);
    this.invalidateIntegrationInfo();
  }

  /**
   * Get events from a specific Google Calendar
   * @param googleCalendarId - ID of the Google Calendar
   * @returns Array of calendar events
   */
  async getCalendarEvents(
    googleCalendarId: string
  ): Promise<Routemize_DTOs_GoogleCalendarEventDto[]> {
    return await GoogleCalendarService.getApiAppGoogleCalendarGetCalendarEvents(googleCalendarId);
  }

  /**
   * Get events for a specific user across all their calendars
   * @param userId - ID of the user
   * @param startDate - Optional start date filter
   * @param endDate - Optional end date filter
   * @returns Array of calendar events
   */
  async getCalendarEventsByUserId(
    userId: string,
    startDate?: string,
    endDate?: string
  ): Promise<Routemize_DTOs_GoogleCalendarEventDto[]> {
    return await GoogleCalendarService.getApiAppGoogleCalendarGetCalendarEventsByUserId(
      userId,
      startDate,
      endDate
    );
  }

  // ---------------------------------------------------------------------------
  // Microsoft / Outlook calendar
  // ---------------------------------------------------------------------------

  /** Initialize Microsoft/Outlook OAuth flow; returns the authorization URL. */
  async getMicrosoftAuthorizationUrl(returnUrl: string): Promise<string> {
    return await MicrosoftCalendarService.getApiAppMicrosoftCalendarGetAuthorizationUrl(returnUrl);
  }

  /** Complete the Microsoft OAuth callback and get the account's available calendars. */
  async completeMicrosoftAuthorization(
    authCode: string,
    stateCode: string
  ): Promise<Routemize_DTOs_MicrosoftCalendarAuthResultDto> {
    return await MicrosoftCalendarService.getApiAppMicrosoftCalendarGetCompleteAuthorization(authCode, stateCode);
  }

  /** Whether the current user has a connected Microsoft/Outlook account. */
  async isConnectedToMicrosoft(): Promise<boolean> {
    return await MicrosoftCalendarService.postApiAppMicrosoftCalendarIsConnectedToMicrosoft();
  }

  /** List the connected Microsoft account's calendars. */
  async getAvailableMicrosoftCalendars(accountEmail?: string): Promise<Routemize_DTOs_MicrosoftCalendarDto[]> {
    return await MicrosoftCalendarService.getApiAppMicrosoftCalendarGetAvailableCalendars(accountEmail);
  }

  /** Connect one Outlook calendar for synchronization. */
  async connectToMicrosoftCalendar(connectionData: Routemize_DTOs_ConnectCalendarToMicrosoftDto): Promise<void> {
    await MicrosoftCalendarService.postApiAppMicrosoftCalendarConnectCalendar(connectionData);
    this.invalidateIntegrationInfo();
  }

  /** Revoke Microsoft access — a specific account, or all when omitted. */
  async revokeMicrosoftAccess(accountEmail?: string): Promise<void> {
    await MicrosoftCalendarService.postApiAppMicrosoftCalendarRevokeMicrosoftAccess(accountEmail);
    this.invalidateIntegrationInfo();
  }

  /** Disconnect a single Outlook calendar; the account's other calendars stay connected. */
  async disconnectFromMicrosoftCalendar(calendarId: string): Promise<void> {
    await MicrosoftCalendarService.postApiAppMicrosoftCalendarDisconnectCalendar(calendarId);
    this.invalidateIntegrationInfo();
  }

  /** Disconnect a single connected calendar via its own provider's endpoint. */
  async disconnectCalendar(calendarId: string, provider: ExternalCalendarType | undefined): Promise<void> {
    if (provider === ExternalCalendarType.MICROSOFT_OUTLOOK) {
      await this.disconnectFromMicrosoftCalendar(calendarId);
    } else {
      await this.disconnectFromGoogleCalendar(calendarId);
    }
  }

  /** Revoke one connected account's access via its own provider's endpoint. */
  async revokeAccountAccess(provider: ExternalCalendarType | undefined, accountEmail?: string): Promise<void> {
    if (provider === ExternalCalendarType.MICROSOFT_OUTLOOK) {
      await this.revokeMicrosoftAccess(accountEmail);
    } else {
      await this.revokeGoogleAccess(accountEmail);
    }
  }

  // Business Logic Methods

  /**
   * Check if a calendar needs re-synchronization based on business rules
   * @param syncStatus - Current sync status
   * @returns True if re-sync is needed
   */
  needsResync(syncStatus: Routemize_DTOs_CalendarSyncStatusDto): boolean {
    if (!syncStatus.isActive) return false;
    
    // Business rule: Re-sync if last sync was more than 1 hour ago
    if (syncStatus.lastSyncTime) {
      const lastSync = new Date(syncStatus.lastSyncTime);
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      if (lastSync < hourAgo) return true;
    }
    
    // Business rule: Re-sync if sync ratio is poor
    if (syncStatus.totalEvents > 0) {
      const syncRatio = syncStatus.syncedEvents / syncStatus.totalEvents;
      if (syncRatio < 0.95) return true;
    }
    
    return false;
  }

  /**
   * Determine optimal sync direction based on calendar usage patterns
   * @param localEventCount - Number of events in local calendar
   * @param googleEventCount - Number of events in Google calendar
   * @returns Recommended sync direction
   */
  recommendSyncDirection(
    localEventCount: number,
    googleEventCount: number
  ): Routemize_Entities_Calendar_External_ExternalCalendarSyncDirection {
    // Business rule: If Google has significantly more events, import only
    if (googleEventCount > localEventCount * 2) {
      return 0; // ImportOnly
    }
    
    // Business rule: If local has significantly more events, export only
    if (localEventCount > googleEventCount * 2) {
      return 1; // ExportOnly
    }
    
    // Otherwise, recommend two-way sync
    return 2; // TwoWay
  }

  /**
   * Validate calendar connection before establishing sync
   * @param calendarId - Local calendar ID
   * @param googleCalendarId - Google calendar ID
   * @throws Error if validation fails
   */
  async validateCalendarConnection(
    calendarId: string,
    googleCalendarId: string
  ): Promise<void> {
    // Check if connected to Google
    const isConnected = await this.isConnectedToGoogle();
    if (!isConnected) {
      throw new Error('Not connected to Google Calendar. Please authorize first.');
    }
    
    // Verify the Google calendar exists and is accessible
    const availableCalendars = await this.getAvailableGoogleCalendars();
    const calendarExists = availableCalendars.some(cal => cal.id === googleCalendarId);
    
    if (!calendarExists) {
      throw new Error(`Google Calendar ${googleCalendarId} not found or not accessible`);
    }
    
    // Check for existing connections to prevent duplicates
    const integrationInfo = await this.getCalendarIntegrationInfo();
    const existingConnection = integrationInfo.connectedCalendars?.find(
      conn => conn.calendarId === calendarId
    );
    
    if (existingConnection) {
      throw new Error(`Calendar ${calendarId} is already connected to Google Calendar`);
    }
  }

  /**
   * Get calendar sync health status with recommendations
   * @param integrationInfo - Current integration information
   * @returns Health status and recommendations
   */
  getCalendarHealthStatus(integrationInfo: Routemize_DTOs_CalendarIntegrationInfoDto): {
    isHealthy: boolean;
    issues: string[];
    recommendations: string[];
  } {
    const issues: string[] = [];
    const recommendations: string[] = [];
    
    // Check connection status
    if (!integrationInfo.isConnectedToGoogle) {
      issues.push('Not connected to Google Calendar');
      recommendations.push('Connect to Google Calendar to enable synchronization');
      return { isHealthy: false, issues, recommendations };
    }
    
    // Check token expiration
    if (integrationInfo.tokenExpirationTime) {
      const expirationDate = new Date(integrationInfo.tokenExpirationTime);
      const daysUntilExpiration = Math.floor(
        (expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysUntilExpiration < 7) {
        issues.push(`Token expires in ${daysUntilExpiration} days`);
        recommendations.push('Re-authenticate with Google Calendar soon');
      }
    }
    
    // Check individual calendar sync status
    for (const calendar of integrationInfo.connectedCalendars || []) {
      if (this.needsResync(calendar)) {
        issues.push(`Calendar ${calendar.calendarName} needs synchronization`);
        recommendations.push(`Synchronize ${calendar.calendarName} to update events`);
      }
      
      if (!calendar.isActive) {
        issues.push(`Calendar ${calendar.calendarName} sync is disabled`);
        recommendations.push(`Enable sync for ${calendar.calendarName} if needed`);
      }
    }
    
    return {
      isHealthy: issues.length === 0,
      issues,
      recommendations
    };
  }

  async getCalendarFeed(): Promise<Routemize_DTOs_CalendarFeedDto | null> {
    return await CalendarFeedService.getApiAppCalendarFeedGetMyFeed();
  }

  async generateCalendarFeed(name?: string): Promise<Routemize_DTOs_CalendarFeedDto> {
    return await CalendarFeedService.postApiAppCalendarFeedGenerateFeed({ name: name ?? 'Routemize Calendar' });
  }

  async revokeCalendarFeed(): Promise<void> {
    await CalendarFeedService.postApiAppCalendarFeedRevokeFeed();
  }
}

// Export singleton instance for backward compatibility
export const calendarManager = new CalendarManager();